import { FC, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as ModalClose } from 'src/assets/svg/modal-close.svg'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import useOutsideClick from 'src/hooks/useOutsideClick'
import classes from './connectAccountModal.module.scss'

export type ConnectAccountPlatform = 'facebook' | 'instagram' | 'whatsapp'

interface ConnectAccountModalProps {
  isOpen: boolean
  onClose: () => void
  onContactUs: () => void
  onSelectPlatform: () => void
}

const WhatsAppIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    height="20"
    viewBox="0 0 24 24"
    width="20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.892 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.49-8.413z"
      fill="currentColor"
    />
  </svg>
)

export const ConnectAccountModal: FC<ConnectAccountModalProps> = ({
  isOpen,
  onClose,
  onContactUs,
  onSelectPlatform,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useOutsideClick({
    onOutsideClick: onClose,
    ref,
  })

  useDisableScroll(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent} ref={ref}>
        <div className={classes.modalHeader}>
          <h2 className={classes.modalTitle}>Connect Account</h2>
          <button
            className={classes.closeButton}
            onClick={onClose}
            type="button"
          >
            <ModalClose />
          </button>
        </div>

        <div className={classes.progressBar}>
          <span className={`${classes.progressSegment} ${classes.active}`} />
          <span className={classes.progressSegment} />
          <span className={classes.progressSegment} />
        </div>

        <p className={classes.description}>
          Connect your Facebook, Instagram, and WhatsApp accounts to start
          seeing insights on incoming messages — broken down by operator and
          platform.
        </p>

        <div className={classes.platformRow}>
          <button
            className={classes.platformButton}
            onClick={onSelectPlatform}
            type="button"
          >
            <FacebookIcon className={classes.platformIcon} />
            <span>Facebook</span>
          </button>
          <button
            className={classes.platformButton}
            onClick={onSelectPlatform}
            type="button"
          >
            <InstagramIcon className={classes.platformIcon} />
            <span>Instagram</span>
          </button>
          <button
            className={classes.platformButton}
            onClick={onSelectPlatform}
            type="button"
          >
            <WhatsAppIcon className={classes.platformIcon} />
            <span>WhatsApp</span>
          </button>
        </div>

        <div className={classes.differentPlatform}>
          <span className={classes.differentPlatformText}>
            + Need a different platform?
          </span>{' '}
          <button
            className={classes.differentPlatformLink}
            onClick={onContactUs}
            type="button"
          >
            Contact us
          </button>
        </div>

        <div className={classes.notice}>
          <div className={classes.noticeTitle}>
            <span className={classes.noticeInfoIcon}>!</span>
            Data availability notice
          </div>

          <div className={classes.noticeRow}>
            <span className={`${classes.pill} ${classes.pillWhatsapp}`}>
              WhatsApp
            </span>
            <span className={classes.noticeText}>
              Historical messages are not available due to platform limitations.
              Only new incoming messages will be analyzed.
            </span>
          </div>

          <div className={classes.noticeRow}>
            <div className={classes.pillGroup}>
              <span className={`${classes.pill} ${classes.pillInstagram}`}>
                Instagram
              </span>
              <span className={`${classes.pill} ${classes.pillFacebook}`}>
                Facebook
              </span>
            </div>
            <span className={classes.noticeText}>
              Messages from the past 6 months are supported.
            </span>
          </div>

          <div className={classes.noticeDivider} />

          <div className={classes.noticeFooter}>
            Need extended historical data?{' '}
            <a className={classes.contactLink} href="mailto:hello@hapttic.com">
              Contact your administrator.
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
